import React, { useState } from 'react';
import { ActivityRendererProps } from './types';

const QuizActivity: React.FC<ActivityRendererProps> = ({ activity, onComplete }) => {
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [selectedAnswers, setSelectedAnswers] = useState<(number | boolean)[]>([]);
  const [showResults, setShowResults] = useState(false);
  const [score, setScore] = useState(0);
  const [showExplanation, setShowExplanation] = useState(false);

  const questions = activity.questions || [];
  const currentQuestion = questions[currentQuestionIndex];

  const handleAnswerSelect = (answerIndex: number | boolean) => {
    const newAnswers = [...selectedAnswers];
    newAnswers[currentQuestionIndex] = answerIndex;
    setSelectedAnswers(newAnswers);
    setShowExplanation(false);
  };

  const handleShowExplanation = () => {
    setShowExplanation(true);
  };

  const handleNextQuestion = () => {
    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
      setShowExplanation(false);
    } else {
      const correctAnswers = selectedAnswers.reduce((count: number, answer, index) => {
        const question = questions[index];
        const isCorrect = answer === question.correct;
        return count + (isCorrect ? 1 : 0);
      }, 0);
      const finalScore = Math.round((correctAnswers / questions.length) * 100);
      setScore(finalScore);
      setShowResults(true);
    }
  };

  const handleCompleteActivity = () => {
    onComplete(score);
  };

  if (showResults) {
    const correctCount = selectedAnswers.reduce((count: number, answer, index) => {
      const question = questions[index];
      const isCorrect = answer === question.correct;
      return count + (isCorrect ? 1 : 0);
    }, 0);

    return (
      <div className="quiz-results">
        <h3>Quiz Results</h3>
        <div className="score-display">
          <span className="score-value">{score}%</span>
          <span className="score-label">
            {correctCount} / {questions.length} correct
          </span>
        </div>
        
        <div className="quiz-review">
          <h4>Review Your Answers:</h4>
          {questions.map((question: any, index: number) => {
            const userAnswer = selectedAnswers[index];
            const isCorrect = userAnswer === question.correct;
            
            return (
              <div key={index} className={`review-question ${isCorrect ? 'correct' : 'incorrect'}`}>
                <div className="review-question-text">
                  <strong>Q{index + 1}:</strong> {question.question}
                </div>
                <div className="review-answer">
                  <span className={`review-status ${isCorrect ? 'correct' : 'incorrect'}`}>
                    {isCorrect ? '✓' : '✗'}
                  </span>
                  {question.type === 'multiple-choice' ? (
                    <span>
                      Your answer: {question.options[userAnswer as number]} 
                      {!isCorrect && (
                        <span className="correct-answer">
                          <br />Correct: {question.options[question.correct]}
                        </span>
                      )}
                    </span>
                  ) : (
                    <span>
                      Your answer: {userAnswer ? 'True' : 'False'}
                      {!isCorrect && (
                        <span className="correct-answer">
                          <br />Correct: {question.correct ? 'True' : 'False'}
                        </span>
                      )}
                    </span>
                  )}
                </div>
                {question.explanation && (
                  <div className="review-explanation">
                    <strong>Explanation:</strong> {question.explanation}
                  </div>
                )}
              </div>
            );
          })}
        </div>
        
        <button className="complete-btn" onClick={handleCompleteActivity}>
          Continue to Next Activity
        </button>
      </div>
    );
  }

  if (!currentQuestion) {
    return (
      <div className="quiz-error">
        <p>No questions available for this quiz.</p>
      </div>
    );
  }

  return (
    <div className="quiz-activity">
      <div className="question-progress">
        Question {currentQuestionIndex + 1} of {questions.length}
      </div>
      
      <div className="question-content">
        <h3 className="question-text">{currentQuestion.question}</h3>
        
        {currentQuestion.type === 'multiple-choice' ? (
          <div className="answer-options">
            {currentQuestion.options.map((option: string, index: number) => (
              <button
                key={index}
                className={`answer-option ${selectedAnswers[currentQuestionIndex] === index ? 'selected' : ''}`}
                onClick={() => handleAnswerSelect(index)}
              >
                {option}
              </button>
            ))}
          </div>
        ) : currentQuestion.type === 'true-false' ? (
          <div className="answer-options">
            <button
              className={`answer-option ${selectedAnswers[currentQuestionIndex] === true ? 'selected' : ''}`}
              onClick={() => handleAnswerSelect(true)}
            >
              True
            </button>
            <button
              className={`answer-option ${selectedAnswers[currentQuestionIndex] === false ? 'selected' : ''}`}
              onClick={() => handleAnswerSelect(false)}
            >
              False
            </button>
          </div>
        ) : null}
        
        {showExplanation && currentQuestion.explanation && (
          <div className="question-explanation">
            <h4>Explanation:</h4>
            <p>{currentQuestion.explanation}</p>
          </div>
        )}
      </div>
      
      <div className="quiz-actions">
        {selectedAnswers[currentQuestionIndex] !== undefined && !showExplanation && currentQuestion.explanation && (
          <button 
            className="explanation-btn"
            onClick={handleShowExplanation}
          >
            Show Explanation
          </button>
        )}
        
        <button 
          className="next-question-btn"
          onClick={handleNextQuestion}
          disabled={selectedAnswers[currentQuestionIndex] === undefined}
        >
          {currentQuestionIndex === questions.length - 1 ? 'Finish Quiz' : 'Next Question'}
        </button>
      </div>
    </div>
  );
};

export default QuizActivity;

